import React, { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { fetchAnalyticsData } from '../../lib/analytics';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const AnalyticsDashboard = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      try {
        const analyticsData = await fetchAnalyticsData(user.id);
        setData(analyticsData);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  if (loading) {
    return <div className="text-center py-8">Loading analytics data...</div>;
  }

  if (!data) {
    return <div className="text-center py-8">No analytics data available</div>;
  }

  return (
    <div className="space-y-8 p-6">
      {/* Entry Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Entry Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800">Total Entries</h3>
            <p className="text-2xl font-bold text-blue-600">{data.entryStats.totalEntries}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800">Paid Entries</h3>
            <p className="text-2xl font-bold text-green-600">{data.entryStats.paidEntries}</p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-800">Pending Entries</h3>
            <p className="text-2xl font-bold text-yellow-600">{data.entryStats.pendingEntries}</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-red-800">Failed Entries</h3>
            <p className="text-2xl font-bold text-red-600">{data.entryStats.failedEntries}</p>
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.entryStats.entriesByDate}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="count" stroke="#8884d8" name="Entries" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Payment Analytics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Payment Analytics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800">Total Revenue</h3>
            <p className="text-2xl font-bold text-purple-600">
              ${data.paymentAnalytics.totalRevenue.toFixed(2)}
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800">Online Payments</h3>
            <p className="text-2xl font-bold text-blue-600">{data.paymentAnalytics.onlinePayments}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800">In-Person Payments</h3>
            <p className="text-2xl font-bold text-green-600">{data.paymentAnalytics.inPersonPayments}</p>
          </div>
        </div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.paymentAnalytics.paymentsByDate}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="amount" fill="#8884d8" name="Revenue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Campaign Performance */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Campaign Performance</h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.campaignPerformance}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="totalEntries" fill="#8884d8" name="Total Entries" />
              <Bar yAxisId="left" dataKey="totalRevenue" fill="#82ca9d" name="Total Revenue" />
              <Line yAxisId="right" type="monotone" dataKey="conversionRate" stroke="#ff7300" name="Conversion Rate" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Prize Distribution */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Prize Distribution</h2>
        <div className="h-80">
          {data.prizeDistribution && Object.keys(data.prizeDistribution.prizesByStatus || {}).length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={Object.entries(data.prizeDistribution.prizesByStatus || {}).map(([status, count]) => ({
                    name: status,
                    value: count
                  }))}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label
                >
                  {Object.entries(data.prizeDistribution.prizesByStatus || {}).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No prize distribution data available
            </div>
          )}
        </div>
      </div>

      {/* Participant Statistics */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold mb-4">Participant Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800">Total Tickets Sold</h3>
            <p className="text-2xl font-bold text-blue-600">
              {data.participantStats?.totalTicketsSold || 0}
            </p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800">Unique Emails</h3>
            <p className="text-2xl font-bold text-green-600">
              {data.participantStats?.uniqueEmails || 0}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800">Entries Per Participant</h3>
            <p className="text-2xl font-bold text-purple-600">
              {data.participantStats?.entriesPerParticipant?.toFixed(2) || '0.00'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard; 